import { useEffect, useRef, useState } from "react";

const InstallPrompt = () => {
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  // Android browsers that can't install apps (e.g. Phoenix): we suggest Chrome
  const [needsChrome, setNeedsChrome] = useState(false);
  const deferred = useRef<any>(null);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true;
    if (standalone) setInstalled(true);

    // iPhone, iPod, or iPad (newer iPads say they are a Mac, but have a touchscreen)
    setIsIos(
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1),
    );

    // Chrome / Edge: the install signal, caught early in index.tsx (or arriving now)
    const pick = () => {
      if ((window as any).__installPrompt) {
        deferred.current = (window as any).__installPrompt;
        setCanInstall(true);
        setNeedsChrome(false);
      }
    };
    pick();
    const onInstalled = () => setInstalled(true);
    window.addEventListener("install-prompt-ready", pick);
    window.addEventListener("appinstalled", onInstalled);

    // On Android, if the browser hasn't offered to install after 4 seconds,
    // it can't install apps, so we suggest opening the page in Chrome
    const chromeTimer = /android/i.test(navigator.userAgent)
      ? window.setTimeout(() => {
          if (!deferred.current) setNeedsChrome(true);
        }, 4000)
      : undefined;

    return () => {
      if (chromeTimer) window.clearTimeout(chromeTimer);
      window.removeEventListener("install-prompt-ready", pick);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (dismissed || installed) return null;
  // Show when the browser can install, on iPhone (steps), or when Chrome is needed
  if (!canInstall && !isIos && !needsChrome) return null;

  const openInChrome = needsChrome && !canInstall;

  const handleInstall = async () => {
    if (deferred.current) {
      await deferred.current.prompt();
      const choice = await deferred.current.userChoice;
      deferred.current = null;
      (window as any).__installPrompt = null;
      setCanInstall(false);
      if (choice?.outcome === "accepted") setInstalled(true);
    } else if (isIos) {
      setShowIosHelp(true);
    } else if (openInChrome) {
      // Opens this same page in Chrome, where it can be installed
      const { host, pathname, search } = window.location;
      window.location.href = `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;end`;
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        maxWidth: "260px",
        width: "calc(100vw - 48px)",
      }}
      className="bg-gray-800 border border-purple-600 rounded-2xl p-4 shadow-2xl"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-white font-semibold text-sm">
          Install TubeScript AI
        </h3>
        <button
          onClick={() => setDismissed(true)}
          className="hamburg text-gray-400 hover:text-white transition text-lg leading-none"
        >
          ✕
        </button>
      </div>
      <p className="text-gray-400 text-xs mb-3">
        {showIosHelp
          ? 'Tap Share (the square with an arrow), then "Add to Home Screen".'
          : openInChrome
            ? "Open in Chrome to install."
            : "Tap to install on your device for a better experience!"}
      </p>
      {!showIosHelp && (
        <button
          onClick={handleInstall}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-xl text-sm font-semibold transition"
        >
          {openInChrome ? "Open in Chrome" : "Install"}
        </button>
      )}
    </div>
  );
};

export default InstallPrompt;
